import { PayloadAction, SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { selectUserQuizId } from '@lib/core/quizzes/selectors';
import {
  selectRemoteAccessCustomId,
  selectRemoteAccessExpiry,
  selectRemoteAccessKey,
  selectRemoteAccessRetries,
} from '@lib/core/retailers/selectors/remoteAccess';
import { selectRetailerId } from '@lib/core/retailers/selectors/retailer';
import {
  apiUrlGenerateAccessKey,
  apiUrlOpenIframeIntegration,
  apiUrlUseAccessKey,
  apiUrlVerifyAccessKey,
} from '@lib/core/retailers/slices/urls';
import { TGenerateRetailerAccessKey } from '@lib/core/retailers/types';
import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';
import { AppStateType } from '@lib/core/service/types/appStateType';
import { uuidv4 } from '@lib/core/service/utils';

export interface IRemoteAccessSlice {
  data: TGenerateRetailerAccessKey;
  list: TGenerateRetailerAccessKey[];
  error: SerializedError;
  isLoading: boolean;
}

const initialState: IRemoteAccessSlice = {
  data: undefined,
  error: null,
  isLoading: false,
  list: undefined,
};

export const actionVerifyRemoteAccessKey = createTypedAsyncThunk(
  'retailers/remoteAccess/verifyKey',
  async (_, { getState }) => {
    const state: AppStateType = getState();

    const remoteExpiry = selectRemoteAccessExpiry(state);
    const remoteAccessKey = selectRemoteAccessKey(state);
    const remoteRetries = selectRemoteAccessRetries(state);
    const remoteCustomId = selectRemoteAccessCustomId(state);
    const retailerId = selectRetailerId(state);
    const userQuizId = selectUserQuizId(state);

    const requestBody = {
      access_key: remoteAccessKey,
      custom_id: remoteCustomId,
      expiry: remoteExpiry.replace(' ', '+'),
      retailer_id: retailerId,
      retries: remoteRetries,
      user_session_id: userQuizId || uuidv4(),
    };

    if (!remoteCustomId) delete requestBody.custom_id;

    return request({ body: requestBody, method: REQUEST_METHODS.POST, url: apiUrlVerifyAccessKey });
  },
);

export const actionUseRemoteAccessKey = createTypedAsyncThunk(
  'retailers/remoteAccess/useKey',
  async (_, { getState }) => {
    const state: AppStateType = getState();

    const remoteExpiry = selectRemoteAccessExpiry(state);
    const remoteAccessKey = selectRemoteAccessKey(state);
    const remoteRetries = selectRemoteAccessRetries(state);
    const remoteCustomId = selectRemoteAccessCustomId(state);
    const retailerId = selectRetailerId(state);
    const userQuizId = selectUserQuizId(state);

    const requestBody = {
      access_key: remoteAccessKey,
      custom_id: remoteCustomId,
      expiry: remoteExpiry.replace(' ', '+'),
      retailer_id: retailerId,
      retries: remoteRetries,
      user_session_id: userQuizId,
    };

    if (!remoteCustomId) delete requestBody.custom_id;

    return request({ body: requestBody, method: REQUEST_METHODS.POST, url: apiUrlUseAccessKey });
  },
);

export const actionGenerateRemoteAccessKey = createTypedAsyncThunk(
  'retailers/remoteAccess/generate',
  async (requestData: { retailer_id: string; retries: string; timestamp: string; custom_id?: string }) => {
    if (!requestData.custom_id) delete requestData.custom_id;
    return await request({ body: requestData, method: REQUEST_METHODS.POST, url: apiUrlGenerateAccessKey });
  },
);

/**
 * * Uses a different key in the store to save links in a list for DevTools
 */
export const actionGenerateRemoteAccessKeyDevTools = createTypedAsyncThunk(
  'retailers/remoteAccess/adminGenerate',
  async (requestData: { retailer_id: string; retries: string; timestamp: string; custom_id?: string }) => {
    if (!requestData.custom_id) delete requestData.custom_id;
    return request({ body: requestData, method: REQUEST_METHODS.POST, url: apiUrlGenerateAccessKey });
  },
);

export const actionSetRemoteAccessParams = createTypedAsyncThunk(
  'retailers/remoteAccess/setParams',
  (params: TGenerateRetailerAccessKey) => params,
);

export const actionValidateOpenIntegration = createTypedAsyncThunk(
  'retailers/remoteAccess/validateOpenIntegration',
  async (requestData: { ciphertext: string; retailer_identifier: string; custom_id?: string }) =>
    request({
      body: requestData,
      method: REQUEST_METHODS.POST,
      url: apiUrlOpenIframeIntegration,
    }),
);

const remoteAccessSlice = createSlice({
  extraReducers: builder => {
    builder.addMatcher(
      isAnyOf(
        actionGenerateRemoteAccessKey.fulfilled,
        actionSetRemoteAccessParams.fulfilled,
        actionValidateOpenIntegration.fulfilled,
      ),
      (state, action: PayloadAction<TGenerateRetailerAccessKey>) => {
        state.data = { ...action.payload, is_valid: true };
        state.error = null;
        state.isLoading = false;
      },
    );
    builder.addMatcher(
      isAnyOf(
        actionGenerateRemoteAccessKey.pending,
        actionUseRemoteAccessKey.pending,
        actionVerifyRemoteAccessKey.pending,
        actionValidateOpenIntegration.pending,
        actionGenerateRemoteAccessKeyDevTools.pending,
      ),
      state => {
        state.error = null;
        state.isLoading = true;
      },
    );

    builder.addMatcher(
      isAnyOf(actionGenerateRemoteAccessKeyDevTools.fulfilled),
      (state, action: PayloadAction<TGenerateRetailerAccessKey>) => {
        state.list = state.list?.length ? [...state.list, action.payload] : [action.payload];
        state.error = null;
        state.isLoading = false;
      },
    );
    builder.addMatcher(
      isAnyOf(actionVerifyRemoteAccessKey.fulfilled, actionUseRemoteAccessKey.fulfilled),
      (state, action: PayloadAction<TGenerateRetailerAccessKey>) => {
        state.data.is_valid = action.payload?.is_valid;
        state.error = null;
        state.isLoading = false;
      },
    );
    builder.addMatcher(
      isAnyOf(
        actionGenerateRemoteAccessKey.rejected,
        actionUseRemoteAccessKey.rejected,
        actionVerifyRemoteAccessKey.rejected,
        actionValidateOpenIntegration.rejected,
        actionGenerateRemoteAccessKeyDevTools.rejected,
      ),
      (state, action) => {
        state.error = { ...action.error, message: 'Access Key Error' };
        state.isLoading = false;
        state.list = undefined;

        if (!state.data) {
          state.data = {
            access_key: undefined,
            custom_id: undefined,
            expiry: undefined,
            is_valid: false,
            retries: undefined,
          };
        } else {
          state.data.is_valid = false;
        }
      },
    );
  },
  initialState,
  name: 'remoteAccess',
  reducers: {
    actionResetRemoteAccess: () => initialState,
  },
});

export default remoteAccessSlice.reducer;

export const { actionResetRemoteAccess } = remoteAccessSlice.actions;
